import { useState } from 'react';

function HelpPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    {
      id: 'getting-started',
      title: 'Primeiros Passos',
      icon: '🚀',
      articles: [
        {
          title: 'Como criar uma nova clínica',
          content: 'Para criar uma nova clínica, acesse Admin > Gerenciar Clínicas e clique em "Nova Clínica". Preencha os dados necessários e clique em "Criar".',
        },
        {
          title: 'Como adicionar médicos',
          content: 'Acesse Admin > Gerenciar Médicos e clique em "Novo Médico". Após criar o médico, você pode associá-lo a uma ou mais clínicas através do botão "Gerenciar Clínicas".',
        },
        {
          title: 'Como cadastrar pacientes',
          content: 'Vá até Admin > Gerenciar Pacientes e clique em "Novo Paciente". Preencha os dados do paciente e associe-o aos médicos que irão atendê-lo.',
        },
      ],
    },
    {
      id: 'appointments',
      title: 'Agendamentos',
      icon: '📅',
      articles: [
        {
          title: 'Como criar um agendamento',
          content: 'Acesse o Calendário e clique em "Novo Agendamento". Selecione a clínica, médico, paciente (opcional), e defina o horário do agendamento.',
        },
        {
          title: 'Filtrar agendamentos',
          content: 'No módulo de Calendário, você pode filtrar agendamentos por clínica, médico, data inicial e data final. Use os filtros na parte superior da página.',
        },
        {
          title: 'Editar ou excluir agendamentos',
          content: 'Na lista de agendamentos, clique em "Editar" para modificar um agendamento ou "Excluir" para removê-lo. A exclusão é permanente e não pode ser desfeita.',
        },
      ],
    },
    {
      id: 'users',
      title: 'Usuários e Permissões',
      icon: '👥',
      articles: [
        {
          title: 'Tipos de usuários',
          content: 'O sistema possui três tipos de usuários: Administrador (acesso total), Médico (visualiza seus próprios pacientes e agendamentos), e Recepcionista (gerencia agendamentos das clínicas associadas).',
        },
        {
          title: 'Como criar um usuário',
          content: 'Acesse Admin > Gerenciar Usuários e clique em "Novo Usuário". Preencha o email, nome, e selecione os papéis (roles) do usuário. Se for um médico, associe ao cadastro do médico correspondente.',
        },
        {
          title: 'Resetar senha de usuário',
          content: 'Na lista de usuários, clique em "Resetar Senha" para forçar a alteração de senha no próximo login do usuário.',
        },
      ],
    },
    {
      id: 'admin',
      title: 'Administração',
      icon: '⚙️',
      articles: [
        {
          title: 'Gerenciar organizações',
          content: 'As organizações agrupam várias clínicas. Para gerenciar organizações, você precisa ter acesso administrativo ao sistema.',
        },
        {
          title: 'Associar médicos a clínicas',
          content: 'Um médico pode atender em múltiplas clínicas. Use o botão "Gerenciar Clínicas" na lista de médicos para fazer essas associações.',
        },
        {
          title: 'Ativar/Desativar registros',
          content: 'Você pode desativar temporariamente clínicas, médicos, usuários e pacientes clicando no badge de status. Registros inativos não aparecem nas seleções mas são preservados no histórico.',
        },
      ],
    },
    {
      id: 'troubleshooting',
      title: 'Solução de Problemas',
      icon: '🔧',
      articles: [
        {
          title: 'Não consigo fazer login',
          content: 'Verifique se suas credenciais estão corretas. Se esqueceu a senha, entre em contato com um administrador para resetá-la. Certifique-se também de que sua conta está ativa.',
        },
        {
          title: 'Erro ao criar registros',
          content: 'Verifique se todos os campos obrigatórios foram preenchidos. Alguns campos como email e CPF devem ser únicos no sistema. Verifique se não existe outro registro com o mesmo valor.',
        },
        {
          title: 'Não vejo meus dados',
          content: 'Suas permissões determinam o que você pode ver. Médicos veem apenas seus pacientes, recepcionistas veem dados das clínicas associadas, e administradores têm acesso completo.',
        },
      ],
    },
    {
      id: 'contact',
      title: 'Contato e Suporte',
      icon: '💬',
      articles: [
        {
          title: 'Como entrar em contato',
          content: 'Para suporte técnico, entre em contato através do email: suporte@clinicas.com.br ou telefone: (00) 0000-0000.',
        },
        {
          title: 'Horário de atendimento',
          content: 'Nosso suporte está disponível de segunda a sexta, das 8h às 18h (horário de Brasília).',
        },
        {
          title: 'Reportar um problema',
          content: 'Ao reportar um problema, forneça o máximo de detalhes possível: o que estava tentando fazer, mensagens de erro recebidas, e quando o problema ocorreu.',
        },
      ],
    },
  ];

  const filteredCategories = categories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      searchQuery === '' ||
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(category => category.articles.length > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Central de Ajuda</h1>
        <p className="text-gray-600 mt-1">
          Encontre respostas para suas dúvidas e aprenda a usar o sistema
        </p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar artigos de ajuda..."
            className="w-full border rounded-lg pl-10 pr-3 py-2"
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Categories Grid */}
      {!selectedCategory && searchQuery === '' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow text-left"
            >
              <div className="text-4xl mb-3">{category.icon}</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {category.title}
              </h3>
              <p className="text-sm text-gray-600">
                {category.articles.length} artigo(s)
              </p>
            </button>
          ))}
        </div>
      )}

      {/* Category Articles or Search Results */}
      {(selectedCategory || searchQuery !== '') && (
        <div className="space-y-4">
          {selectedCategory && searchQuery === '' && (
            <button
              onClick={() => setSelectedCategory(null)}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <svg
                className="w-5 h-5 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Voltar para categorias
            </button>
          )}

          {filteredCategories.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">
                Nenhum artigo encontrado para sua busca.
              </p>
            </div>
          ) : (
            filteredCategories
              .filter(cat => !selectedCategory || cat.id === selectedCategory)
              .map((category) => (
                <div key={category.id} className="space-y-4">
                  {searchQuery !== '' && (
                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                      <span className="text-2xl mr-2">{category.icon}</span>
                      {category.title}
                    </h2>
                  )}
                  {category.articles.map((article, index) => (
                    <details
                      key={index}
                      className="bg-white rounded-lg shadow overflow-hidden"
                    >
                      <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 font-medium text-gray-900 flex items-center justify-between">
                        <span>{article.title}</span>
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </summary>
                      <div className="px-6 py-4 bg-gray-50 border-t">
                        <p className="text-gray-700">{article.content}</p>
                      </div>
                    </details>
                  ))}
                </div>
              ))
          )}
        </div>
      )}

      {/* Contact Support Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          Não encontrou o que procurava?
        </h3>
        <p className="text-blue-800 mb-4">
          Nossa equipe de suporte está pronta para ajudar você.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href="mailto:suporte@clinicas.com.br"
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Enviar Email
          </a>
          <a
            href="tel:+5500000000000"
            className="inline-flex items-center justify-center px-4 py-2 bg-white border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            Ligar para Suporte
          </a>
        </div>
      </div>
    </div>
  );
}

export default HelpPage;
